<template>
  <article>
    <activity-stage :customRootContainerClass="'painted-finger-clue'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <!-- eslint-disable vue/no-parsing-error -->
        <figure
          :key="currentKey"
          :class="
            `keyboard-k2 painted-finger-clue-keys focusable ${
              oneHandedTypingHand ? 'one-hand-typing-hand' : ''
            } ${isMobile ? 'mobile' : 'desktop'} ${isChromeOS ? 'chrome-os' : ''}`
          "
          :style="{ margin: oneHandedTypingHand ? '0px' : `0px 0px 0px ${marginLeftCurrentKey}px` }"
          tabindex="0"
        >
          <div :class="currentKey" :style="{ backgroundColor: currentKeyColor }"></div>
        </figure>

        <div
          class="painted-finger-clue-container"
          :class="!showBothHands ? 'painted-one-hand' : ''"
          :style="{
            visibility:
              progress && (showBothHands || holdKeyPressed || oneHandedTypingHand)
                ? 'visible'
                : 'hidden'
          }"
        >
          <div
            v-if="isVisibleLeftHand"
            :class="{ 'painted-finger-clue-left-side': oneHandedTypingHand ? false : true }"
          >
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="leftHand"></svg>
            </div>
          </div>

          <div
            v-if="isVisibleRightHand"
            :class="{ 'painted-finger-clue-right-side': oneHandedTypingHand ? false : true }"
          >
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="rightHand"></svg>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import KeyToFingerHelper from 'mixins/KeyToFingerHelper';

import { convertSymbolToName } from 'utils/key-util';
import { mapState } from 'vuex';

const lettersQueues = {
  nrow: '`1234567890-=',
  qrow: 'qwertyuiop[]',
  arow: "asdfghjkl;'\\",
  zrow: 'zxcvbnm,./'
};

const targetColors = {
  nrow: '#FAD4A4',
  qrow: '#FFF6CB',
  arow: '#DFEBBE',
  zrow: '#D2E6EE',
  rowSpace: '#d4d1e3'
};

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  components: {
    ActivityStage
  },
  mixins: [
    ActivityManager,
    TypingActivityHelper,
    TouchAndHoldHelper,
    ErrorLogger,
    KeyToFingerHelper
  ],
  data() {
    return {
      activityName: 'PaintedFingerClues',
      showBothHands: false,
      displayHand: '',
      currentKey: undefined,
      touchAndHold: false,
      needsSwitch: false
    };
  },

  computed: {
    ...mapState('keyboard', ['showSpecialKeys']),

    marginLeftCurrentKey() {
      let marginLeft = this.marginsLeftForCurrentKey.LHEmpty;
      if (this.isVisibleLeftHand) {
        // eslint-disable-next-line prefer-destructuring
        marginLeft = this.leftHand.marginLeftCurrentKey;
      }

      if (
        this.isVisibleRightHand &&
        this.rightHand.marginLeftCurrentKey !== this.marginsLeftForCurrentKey.LHEmpty
      ) {
        // eslint-disable-next-line prefer-destructuring
        marginLeft = this.rightHand.marginLeftCurrentKey;
      }

      if (!marginLeft) {
        marginLeft = this.marginsLeftForCurrentKey.LHEmpty;
      }
      return marginLeft;
    },

    isVisibleLeftHand() {
      return this.showHand('left');
    },

    leftHand() {
      return this.getHand({
        key: this.expectedKey,
        oneHandedTypingHand: this.oneHandedTypingHand,
        side: 'left'
      });
    },

    isVisibleRightHand() {
      return this.showHand('right');
    },

    rightHand() {
      return this.getHand({
        key: this.expectedKey,
        oneHandedTypingHand: this.oneHandedTypingHand,
        side: 'right'
      });
    },

    currentKeyColor() {
      if (this.currentStep === ' ') return targetColors.rowSpace;
      if (this.currentKey === 'keyabc switchKey' || this.currentKey === 'key123 switchKey')
        return targetColors.rowSpace;

      let keyColor = '';

      Object.entries(lettersQueues).some(([key, value]) => {
        if ([...value].includes(this.currentStep)) {
          keyColor = key;
          return true;
        }
        return false;
      });

      return targetColors[keyColor];
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },

    getKeyboardConfig() {
      const { letters } = this.rounds[0];
      const hideNumberRow = !this.activityData.keyboard.showNumbers;
      const { keyboardType, roofs, characters } = this.activityData.keyboard;

      const containsNumbers = /\d/.test(letters);

      const keyboardConfig = {
        showTargets: true,
        keys:
          this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard
            ? null
            : characters,
        isTouchHold:
          this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard
            ? false
            : this.touchAndHold,
        hideNumberRow,
        keyboardType,
        roofs
      };

      return keyboardConfig;
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const { characters } = this.activityData.keyboard;

      const containsNumbers = characters && /\d/.test(characters);

      this.touchAndHold =
        (this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard) ||
        this.oneHandedTypingHand
          ? false
          : characters.includes('F') !== characters.includes('J');

      return {
        rounds: rounds.map(r => ({
          ...r,
          isTouchHold: this.oneHandedTypingHand ? false : undefined
        })),
        stepIndicator: 'keystrokes'
      };
    },

    stepCallback() {
      return this.currentStep;
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      if (this.touchAndHold) this.displayHand = this.targetKey === 'j' ? 'left' : 'right';

      if (!this.touchAndHold) {
        this.showBothHands = true;
        this.displayHand = '';
      }

      this.currentKey = this.getCurrentKey();
    },

    showHand(hand) {
      return (
        (this.showBothHands || this.displayHand === hand) &&
        (!this.oneHandedTypingHand || this.oneHandedTypingHand === `${hand}-hand`)
      );
    },

    getHand(params) {
      const { key, oneHandedTypingHand, side } = params;
      const handImages = this[oneHandedTypingHand ? 'oneHandedTypingHands' : 'hands'][side].images;
      if (this.needsSwitch) return handImages.empty.svg;
      return handImages[key]?.svg || handImages.empty.svg;
    },

    getCurrentKey() {
      let currentKey = '';
      const { currentStep } = this;

      const key =
        `key${convertSymbolToName(currentStep)}` || `special-${convertSymbolToName(currentStep)}`;

      const keyElement = document.getElementsByClassName(key)[0];

      if (keyElement) {
        this.needsSwitch = false;
        currentKey = keyElement.classList.value;
      }

      if (key && !keyElement) {
        this.needsSwitch = true;
        currentKey = `${/\d/.test(key) ? 'key123' : 'keyabc'} switchKey`;
      }

      return `${currentKey}${
        this.marginLeftCurrentKey > 40 && this.marginLeftCurrentKey <= 330 ? ' leftHandKey' : ''
      }${this.marginLeftCurrentKey > 40 && this.marginLeftCurrentKey > 330 ? ' rightHandKey' : ''}`;
    },

    handleCorrectKey(attempt) {
      if (this.isLastStepInRound) {
        this.finishRound();
        return;
      }

      this.sndType();
      this.captureKeyboardAttemptSegment(attempt, true);

      this.setCurrentStep({ stepCallback: this.stepCallback });
      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();
      });
    },

    finishRound() {
      this.sndBell();

      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.startRound();
      }
    }
  },

  watch: {
    expectedKey(newKey) {
      let instructions = `Type the ${newKey} key.`;

      this.setCurrentKeyboardingSegment('');
      this.setTotalKeyboardingSegment(newKey);
      this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
    },
    showSpecialKeys() {
      this.needsSwitch = false;
      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();
      });
    }
  }
};
</script>

<style lang="scss">
@import '../assets/sass/keyboards/partials/keyboardk2art.scss';
@import '../assets/sass/_zoom-reflow.scss';

.keyboard-k2 .switchKey {
  padding-top: 48%;
  height: 0;
  background-size: cover;
  background-repeat: no-repeat;
  width: 150px;
  background-color: rgb(212, 209, 227);
}

.painted-finger-clue {
  position: relative;

  .active.target {
    &:after {
      visibility: hidden;
    }
  }
}

.painted-finger-clue-keys {
  display: block;
  width: 130px;
  height: 130px;

  @media (max-width: 863px) {
    display: flex;
    justify-content: center;
    margin: auto !important;
    max-width: 500px;
    text-align: center;
    width: 100%;

    &:has(> div.leftHandKey) {
      justify-content: flex-start;
    }

    &:has(> div.rightHandKey) {
      justify-content: flex-end;
    }

    div.leftHandKey {
      left: calc(125px - 65px);
    }

    div.rightHandKey {
      right: calc(125px - 65px);
    }

    div {
      height: 130px;
      position: relative;
      width: 130px;
    }
  }

  div {
    height: 100%;
    background-size: contain;
  }
}

.one-hand-typing-hand {
  position: relative;
  left: 50%;
  transform: translateX(-50%);
}

.painted-finger-clue-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;

  &.painted-one-hand {
    .painted-finger-clue-left-side {
      margin-right: 200px;
    }

    .painted-finger-clue-right-side {
      margin-left: 200px;
    }
  }

  svg {
    height: 225px;
    width: 225px;

    @include ifMaxWidth(690px) {
      height: 150px;
      width: 150px;
    }
  }
}

.chrome-os {
  .painted-finger-clue-keys {
    height: 100px;
    width: 100px;
  }
}
</style>
